/**
 * Body Part Normalization Utility
 *
 * This utility normalizes body part names from DICOM/PACS data into
 * standardized anatomical regions for consistent analytics reporting.
 */

export const BODY_PART_MAPPING: Record<string, string> = {
  // Head/Brain/Skull
  'HEAD': 'HEAD',
  'BRAIN': 'HEAD',
  'SKULL': 'HEAD',
  'BRAIN CONT': 'HEAD',
  'ORBIT': 'HEAD',

  // Chest/Thorax - Keep separate for modality-specific analysis
  'CHEST': 'CHEST',
  'THORAX': 'CHEST',
  'CARDIAC': 'CHEST',
  'HEART': 'CHEST',

  // Spine - All variations
  'SPINE': 'SPINE',
  'CSPINE': 'SPINE',
  'TSPINE': 'SPINE',
  'LSPINE': 'SPINE',

  // Abdomen
  'ABDOMEN': 'ABDOMEN',
  'MRCP': 'ABDOMEN',

  // Pelvis
  'PELVIS': 'PELVIS',
  'PROSTATE': 'PELVIS',

  // Upper Extremity
  'UPPER LIMB': 'UPPER EXTREMITY',
  'UPPER EXTREMITY': 'UPPER EXTREMITY',
  'SHOULDER': 'UPPER EXTREMITY',
  'RT SHOULDER': 'UPPER EXTREMITY',
  'LT SHOULDER': 'UPPER EXTREMITY',
  'ELBOW': 'UPPER EXTREMITY',

  // Lower Extremity
  'LOWER LIMB': 'LOWER EXTREMITY',
  'EXTREMITY': 'LOWER EXTREMITY',
  'KNEE': 'LOWER EXTREMITY',
  'RT KNEE': 'LOWER EXTREMITY',
  'LT KNEE': 'LOWER EXTREMITY',
  'LEG': 'LOWER EXTREMITY',
  'RT LEG': 'LOWER EXTREMITY',
  'THIGH': 'LOWER EXTREMITY',
  'FOOT': 'LOWER EXTREMITY',
  'ANKLE': 'LOWER EXTREMITY',
  'RT ANKLE': 'LOWER EXTREMITY',

  // Neck
  'NECK': 'NECK',

  // Special cases
  'STITCH': 'OTHER',
  '<Empty>': 'UNKNOWN',
  'Unknown': 'UNKNOWN',
};

/**
 * Normalizes a body part name to a standardized region
 * @param bodyPart - Raw body part name from DICOM/PACS
 * @returns Normalized body part name
 */
export const normalizeBodyPart = (bodyPart: string | null | undefined): string => {
  if (!bodyPart) return 'UNKNOWN';

  const upperBodyPart = bodyPart.toUpperCase().trim();
  return BODY_PART_MAPPING[upperBodyPart] || upperBodyPart;
};

/**
 * Gets all unique normalized body parts from an array of raw body parts
 * @param bodyParts - Array of raw body part names
 * @returns Array of unique normalized body parts
 */
export const getUniqueNormalizedBodyParts = (bodyParts: (string | null)[]): string[] => {
  const normalized = bodyParts.map(bp => normalizeBodyPart(bp));
  return Array.from(new Set(normalized)).sort();
};

/**
 * Priority order for body parts in UI (most common/important first)
 */
export const BODY_PART_PRIORITY_ORDER: string[] = [
  'HEAD',
  'CHEST',
  'ABDOMEN',
  'SPINE',
  'PELVIS',
  'LOWER EXTREMITY',
  'UPPER EXTREMITY',
  'NECK',
  'OTHER',
  'UNKNOWN'
];

/**
 * Default body parts to show for each modality based on clinical relevance
 */
export const DEFAULT_BODY_PARTS_BY_MODALITY: Record<string, string[]> = {
  'CT': ['HEAD', 'CHEST', 'ABDOMEN', 'SPINE', 'PELVIS'],
  'MR': ['HEAD', 'SPINE', 'LOWER EXTREMITY', 'ABDOMEN'],
  'DX': ['CHEST', 'SPINE', 'LOWER EXTREMITY', 'UPPER EXTREMITY'],
  'US': ['ABDOMEN', 'PELVIS'],
};

/**
 * Ideal performance benchmarks (median ms per instance) from historical data
 * These values are used to draw the "ideal service line" on scatter plots
 */
export const IDEAL_PERFORMANCE_BENCHMARKS: Record<string, Record<string, number>> = {
  'CT': {
    'HEAD': 749,
    'CHEST': 790,
    'ABDOMEN': 749,
  },
  'MR': {
    'HEAD': 279,
    'SPINE': 539,
    'BRAIN': 510,
  },
  'DX': {
    'CHEST': 743,
    'SPINE': 614,
    'LOWER EXTREMITY': 522,
    'UPPER EXTREMITY': 508,
  }
};

/**
 * Service line slopes (90th percentile seconds per instance)
 * Calculated from last month's data - represents good performance threshold
 * Line equation: load_time = slope * instances
 * 90% of studies should fall below this line
 *
 * Last calculated: January 2026
 * Based on studies from last 30 days, excluding outliers >150s
 */
export const SERVICE_LINE_SLOPES: Record<string, Record<string, number>> = {
  'CT': {
    'HEAD': 0.05,         // 50 ms per instance (30s for 600 instances) - TARGET
    'CHEST': 0.05,        // 50 ms per instance (45s for 900 instances) - TARGET
    'ABDOMEN': 0.0411,    // 41.1 ms per instance (37s for 900 instances) - TARGET
    'PELVIS': 0.9155,     // 915.5 ms per instance (6 studies) - REAL DATA
    'NECK': 0.0487,       // 48.7 ms per instance (6 studies) - REAL DATA
    'LOWER EXTREMITY': 0.012, // 12 ms per instance (12s for 1000 instances) - TARGET
    'UPPER EXTREMITY': 1.7051, // 1.7 seconds per instance - using EXTREMITY value
  },
  'MR': {
    'HEAD': 0.0378,       // Average of BRAIN (0.0524) and HEAD (0.0231) = 37.8 ms per instance - REAL DATA
    'SPINE': 0.0658,      // Average of SPINE (0.0508) and LSPINE (0.0807) = 65.8 ms per instance - REAL DATA
    'ABDOMEN': 0.006,     // 6 ms per instance (6s for 1000 instances) - TARGET
    'PELVIS': 0.02,       // 20 ms per instance (12s for 600 instances) - TARGET
    'LOWER EXTREMITY': 0.0590, // Average of KNEE (0.0433) and LT KNEE (0.0746) = 59 ms per instance - REAL DATA
    'UPPER EXTREMITY': 0.0450, // 45 ms per instance (5 studies) - REAL DATA
  },
  'DX': {
    'CHEST': 5.2432,      // 5.24 seconds per instance (387 studies) - THORAX normalized - REAL DATA
    'SPINE': 3.0880,      // 3.09 seconds per instance (66 studies) - REAL DATA
    'LOWER EXTREMITY': 4.4761, // 4.48 seconds per instance (100 studies) - LOWER LIMB normalized - REAL DATA
    'UPPER EXTREMITY': 2.3698, // 2.37 seconds per instance (25 studies) - UPPER LIMB normalized - REAL DATA
    'HEAD': 1.6873,       // 1.69 seconds per instance (16 studies) - SKULL normalized - REAL DATA
    'ABDOMEN': 10.9945,   // 11.0 seconds per instance (26 studies) - REAL DATA
  }
};

/**
 * Gets the ideal performance benchmark for a modality-bodypart combination
 * @param modality - Modality (CT, MR, DX, etc.)
 * @param bodyPart - Normalized body part name
 * @returns Benchmark value in ms/instance, or null if not defined
 */
export const getPerformanceBenchmark = (modality: string, bodyPart: string): number | null => {
  return IDEAL_PERFORMANCE_BENCHMARKS[modality]?.[bodyPart] || null;
};

/**
 * Gets the service line slope (90th percentile performance) for a modality-bodypart combination
 * @param modality - Modality (CT, MR, DX, etc.)
 * @param bodyPart - Normalized body part name
 * @returns Slope value in seconds/instance, or null if not defined
 */
export const getServiceLineSlope = (modality: string, bodyPart: string): number | null => {
  return SERVICE_LINE_SLOPES[modality]?.[bodyPart] || null;
};
